import * as R from 'ramda';
import * as React from 'react';
import { useMemo, useEffect, useCallback } from 'react';
import { css } from '@emotion/core';
import { COLORS } from '../../../globalStyle/colors';
import Msg from '../../../localization/Msg';
import { Str } from '../../../localization/translationKeys';
import { Rater, RaterLevel } from './models';
import { isValidDate } from '../../../language/time';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { useWindowSize } from '../../../core/hooks/useWindowSize';
import { onMobileDevice } from '../../../globalStyle/mediaQueries';
import RaterTableRow from './RaterTableRow';
import RaterEditableTableRow from './RaterEditableTableRow';
import { Language } from '../../../localization/models';
import { useFormik } from 'formik';
import { editRaterFormValidation, editRaterWithNamesFormValidation } from './formValidations';
import RaterEditableCard from './RaterEditableCard';
import RaterCard from './RaterCard';
import { FLEX_ROW } from '../../../layout/flexBox';
import { useMsgText } from '../../../localization/hooks';
import Spacer from '../../../layout/Spacer';
import LocalDiv from '../../../localization/LocalDiv';
import { toMap } from '../../../language/arrays';
import { isNilOrEmpty } from '../../../language/module';
import { Requirement } from '../../models';
import { canAddRaterForLevel } from '../../module';


const styles = {
  table:        {
    root:           css`
      table-layout: auto;
      width: 100%;
      border-collapse: collapse;

      & th, & td {
        padding: 18px 4px;
        border: 1px solid;
        border-color: ${COLORS.LIGHTER_GREY} ${COLORS.LIGHTEST_GREY};

        // first/last-of-type rather than -child: rows here are all th or all td, so they select the same
        // cells, and Emotion warns on :first-child as SSR-unsafe.
        &:first-of-type:dir(rtl), &:last-of-type:dir(ltr) {
          padding-right: 12px;
        }

        &:first-of-type:dir(ltr), &:last-of-type:dir(rtl) {
          padding-left: 12px;
        }
      }
    `,
    headerWithData: css`
      & th {
        border-bottom: 0;
      }
    `,
    row:            css`
      color: ${COLORS.DARK_GREY};
      background-color: ${COLORS.LIGHTEST_GREY};
    `,
    errorRow:       css`
      color: ${COLORS.RED};
    `,
    editableRow:    css`
      background-color: ${COLORS.WHITE};
      & th, & td {
        border-color: ${COLORS.LIGHTER_GREY} ${COLORS.WHITE};
      }
    `,
  },
  cells:        {
    emailOrName: css`
      min-width: min-content;
    `,
    edit:        css`
      max-width: 50px;
      min-width: 30px;
    `,
    delete:      css`
      max-width: 50px;
      min-width: 30px;
    `,
  },
  errorMsg:     {
    root:    css`
      align-items: center;
      ${onMobileDevice} {
        display: inline;
        margin: 0;
      }
    `,
    icon:    css`
      font-size: 18px;
      ${onMobileDevice} {
        font-size: unset;
      }
    `,
    msgText: css`
      background-color: transparent;
      padding: 10px 5px;
      font-style: italic;
      ${onMobileDevice} {
        font-size: unset;
      }
    `,
  },
  infoRow:      css`
    display: flex;
    flex-direction: row;
  `,
  cardInfoRow:  css`
  display: flex;
  flex-direction: row;
  margin: 10px;
`, radio: css`
    margin: 0;
  `,
  card:         css`
    margin: 8px 0;
    padding: 15px;
    border: 1px solid transparent;
    background-color: ${COLORS.LIGHTEST_GREY};
    font-size: 12px;
  `,
  errorCard:    css`
    color: ${COLORS.RED};
  `,
  editableCard: css`
    margin: 8px 0;
    padding: 16px;
    background-color: ${COLORS.WHITE};
    * {
      font-size: 12px;
    }
  `,
  label:        css`
    font-weight: bold;
  `,
};


type Props = {
  className?: string;
  raters: Rater[];
  raterLevels: RaterLevel[];
  projectLanguages: Language[];
  undeliveredEmails: string[];
  code: string;

  // Edit rater data state
  raterUnderEdit: Rater | undefined;
  markRaterForEdit: (rater: Rater | undefined) => void;
  cancelRaterEdit: () => void;
  desyncedRaterId?: number;
  requirements?: Requirement[];
  maximumsRequired: boolean;

  showNameFields: boolean;
  approval: boolean;
  hideApproverColumn: boolean;
  approvalLevelId: number;
  approvalLevelCount: number;
  approverEmailSent: boolean;

  setErrorMsg: (errorMsg?: string) => void;
  updateRater: (
    raterId: number,
    email?: string,
    undelivered?: boolean,
    firstName?: string,
    lastName?: string,
    raterLevel?: RaterLevel,
    language?: Language,
  ) => void;
  deleteRater: (raterId: number) => void;
  tableAlign: string;
  setApprover: (raterId: number) => void;
};

// TODO: set date from app locale, not browser locale
const inviteStatus = (
  inviteDate: string | Date,
  emailBounced: boolean,
  statusMsgKey: string,
  approval: boolean,
  approvalStatus: number,
  code: string,
) => {
  const fallbackMsgColor = approval ? COLORS.BLACK : COLORS.GREEN;

  // Text for special status messages is black with no icon if rater approval is required
  // Undelivered status messages are red in both cases
  return (
    emailBounced
    ? <LocalDiv css={[FLEX_ROW, styles.errorMsg.root, css`color: ${COLORS.RED};`]}>
        <FontAwesomeIcon icon={faExclamationTriangle} css={styles.errorMsg.icon} size={'1x'} />
        <Msg str={statusMsgKey} css={styles.errorMsg.msgText} />
      </LocalDiv>
    : approval && approvalStatus === 1
      ? <span>---</span>
      : isValidDate(inviteDate)
        ? <span>{new Date(inviteDate).toLocaleDateString(code)}</span>
        : <LocalDiv css={[FLEX_ROW, styles.errorMsg.root, css`color: ${fallbackMsgColor};`]}>
            {!approval && (<FontAwesomeIcon icon={faExclamationTriangle} css={styles.errorMsg.icon} size={'1x'} />)}
            <Msg str={statusMsgKey} css={[styles.errorMsg.msgText]} />
          </LocalDiv>
  );
};

const msgForApprovalStatus = (status: number) => (
  status === 5 // waiting for approval
    ? Str.ApprovalStatus5
    : status === 3 // approved
      ? Str.ApprovalStatus3
      : status === 4 // declined
        ? Str.ApprovalStatus4
        : Str.ApprovalStatus1 // approver
);

const approvalStatus = (status: number, potentialApprover: boolean) => (
  <>
    {
      status === 1
        ? '---'
        : (
          status === 2 || status === 0  // needs approval
            ? (
              <LocalDiv css={[FLEX_ROW, styles.errorMsg.root]}>
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  css={[
                    styles.errorMsg.icon,
                    css`color: ${COLORS.GREEN}`,
                  ]}
                  size={'1x'}
                />
                <Msg
                  str={Str.ApprovalStatus2}
                  css={[styles.errorMsg.msgText, css`color: ${COLORS.GREEN}`]}
                />
              </LocalDiv>
            )
            : (
              <Msg str={msgForApprovalStatus(status)} />
            )
        )
    }
  </>
);

const approverOption = (
  selected: boolean,
  raterId: number,
  setApprover: (raterId: number) => void,
  approverEmailSent: boolean,
) => (
  <LocalDiv css={styles.infoRow}>
    {
      !approverEmailSent && (
        <>
          <input
            type='radio'
            name='approver'
            css={styles.radio}
            checked={selected}
            onChange={() => setApprover(raterId)}
          />
          <Spacer sizePx={10} />
        </>
      )
    }
    <Msg str={selected ? Str.Yes : Str.No} />
  </LocalDiv>
);

const approver = (
  status: number,
  level: number,
  approvalLevel: number,
  levelCount: number,
  raterId: number,
  setApprover: (raterId: number) => void,
  approverEmailSent: boolean,
) => status === 1
  ? (
    levelCount === 1
      ? <Msg str={Str.Yes} />
      : approverOption(true, raterId, setApprover, approverEmailSent)
  )
  : (
    levelCount > 1 && level === approvalLevel
      ? approverOption(false, raterId, setApprover, approverEmailSent)
      : '---'
  );


const approvalCardElements = (
  approval: boolean,
  hideApproverColumn: boolean,
  status: number,
  level: number,
  approvalLevel: number,
  levelCount: number,
  raterId: number,
  setApprover: (raterId: number) => void,
  approverEmailSent: boolean,
) =>
  (
    approval && (
      <>
        <LocalDiv css={[FLEX_ROW, styles.cardInfoRow]}>
          <Msg css={styles.label} str={Str.ApprovalColon} />
          <Spacer sizePx={8} />
          {approvalStatus(
            status,
            approvalLevel === level,
          )}
        </LocalDiv>
        {
          !hideApproverColumn && (
            <LocalDiv css={[FLEX_ROW, styles.cardInfoRow]}>
              <Msg css={styles.label} str={Str.ApproverColon} />
              <Spacer sizePx={8} />
              {
                approver(
                  status,
                  level,
                  approvalLevel,
                  levelCount,
                  raterId,
                  setApprover,
                  approverEmailSent,
                )
              }
            </LocalDiv>
          )
        }
      </>
    )
  );

const approvalTableElements = (
  approval: boolean,
  hideApproverColumn: boolean,
  status: number,
  level: number,
  approvalLevel: number,
  levelCount: number,
  raterId: number,
  setApprover: (raterId: number) => void,
  approverEmailSent: boolean,
) =>
  (
    approval && (
      <>
        <td>
          {approvalStatus(
            status,
            approvalLevel === level,
          )}
        </td>
        {
          !hideApproverColumn && (
            <td>
              {
                approver(
                  status,
                  level,
                  approvalLevel,
                  levelCount,
                  raterId,
                  setApprover,
                  approverEmailSent,
                )
              }
            </td>
          )
        }
      </>
    )
  );

/**
 * ViewRaters: a component that renders a table for the user to view their raters
 *
 * PureComponent: true
 * Redux Connected: false
 */
const RaterList: React.FC<Props> = props => {
  const { setErrorMsg } = props;

  const [underEdit, setUnderEdit] = [props.raterUnderEdit, props.markRaterForEdit];

  const { isMobile } = useWindowSize();

  const invalidEmail = useMsgText(Str.InvalidEmail);

  const languagesById = React.useMemo(
    () => toMap(props.projectLanguages ?? [], lang => `${lang.languageId}`),
    [props.projectLanguages],
  );

  const raterLevelsById = React.useMemo(
    () => toMap(props.raterLevels ?? [], lang => `${lang.surveyLevelId}`),
    [props.raterLevels],
  );

  const raterUpdateFailedFor = useCallback(
    (rater: Rater) => rater.raterId === props.desyncedRaterId,
    [props.desyncedRaterId]);

  const maximumRatersAdded = useMsgText(Str.MaxRatersMsg);

  const acceptableRaterLevelIds = useMemo(
    () => {
      if (isNilOrEmpty(props.requirements)) {
        return undefined;
      }

      const applicableRequirements = R.filter(
        req => canAddRaterForLevel(props.maximumsRequired, req),
        props.requirements,
      );

      return R.map(req => req.surveyLevelId, applicableRequirements);
    },
    [props.maximumsRequired, props.requirements],
  );

  const maxRatersErrMsg = useCallback(
    (levelId?: number) => {
      if (!levelId) {
        return '';
      }

      const currentRequirement = props.requirements?.find(req => req.surveyLevelId === levelId);
      const reqName     = currentRequirement?.fullName ?? '';

      return maximumRatersAdded.replace('{levelName}', reqName);
    },
    [maximumRatersAdded, props.requirements],
  );

  const { resetForm, errors, handleSubmit, handleChange, values } = useFormik({
    initialValues:      props.showNameFields ? {
      email:       underEdit?.email || '',
      undelivered: underEdit?.undeliveredEmail || false,
      firstName:   underEdit?.firstName || '',
      lastName:    underEdit?.lastName || '',
      levelId:     underEdit?.levelId || 0,
      languageId:  underEdit?.languageId || 0,
    } : {
      email:       underEdit?.email || '',
      undelivered: underEdit?.undeliveredEmail || false,
      levelId:     underEdit?.levelId || 0,
      languageId:  underEdit?.languageId || 0,
    },
    onSubmit:           (values) => {
      props.updateRater(
        underEdit!.raterId,
        values.email || undefined,
        values.undelivered || undefined,
        props.showNameFields ? values.firstName : undefined,
        props.showNameFields ? values.lastName : undefined,
        raterLevelsById[`${values.levelId ?? -1}`],
        languagesById[`${values.languageId ?? -1}`],
      );
      resetForm();
      setUnderEdit(undefined);
    },
    validationSchema: props.showNameFields
                        ? editRaterWithNamesFormValidation(
                            invalidEmail,
                            (levelId) => maxRatersErrMsg(levelId),
                            acceptableRaterLevelIds,
                          )
                        : editRaterFormValidation(
                            invalidEmail,
                            (levelId) => maxRatersErrMsg(levelId),
                            acceptableRaterLevelIds,
                          ),
    validateOnChange:   false,
    validateOnBlur:     false,
    enableReinitialize: true,
  });

  useEffect(
    () => setErrorMsg(
      errors.email || errors.firstName || errors.lastName || errors.levelId || errors.languageId || undefined,
    ),
    [errors.email, errors.firstName, errors.languageId, errors.lastName, errors.levelId, setErrorMsg],
  );

  const showRelationshipColumn = props.raterLevels?.length > 1;
  const showLanguageColumn     = props.projectLanguages?.length > 1;

  const highlightEmailInput = useMemo(
    () => props.undeliveredEmails.indexOf(values.email) > -1,
    [props.undeliveredEmails, values.email],
  );

  const disableSubmitEditBtn = useMemo(
    () => props.undeliveredEmails.indexOf(values.email) > -1,
    [props.undeliveredEmails, values.email],
  );

  const mapRatersToCards = useMemo(
    () => {
      const inEditMode = (raterId: number) => underEdit?.raterId === raterId;

      return R.map(
        (rater: Rater) =>
          inEditMode(rater.raterId) ? (
            <RaterEditableCard
              key={rater.raterId}
              css={[styles.card, raterUpdateFailedFor(rater) && styles.errorCard]}
              rater={rater}
              showNames={props.showNameFields}
              approval={props.approval}
              inviteStatus={inviteStatus(
                rater.invitedDate,
                rater.undeliveredEmail,
                rater.statusMessageKey,
                props.approval,
                rater.approvalStatus,
                props.code,
              )}
              approvalElements={approvalCardElements(
                props.approval,
                props.hideApproverColumn,
                rater.approvalStatus,
                rater.levelId,
                props.approvalLevelId,
                props.approvalLevelCount,
                rater.raterId,
                props.setApprover,
                props.approverEmailSent,
              )}
              setRater={setUnderEdit}
              cancelSetRater={props.cancelRaterEdit}
              raterLevels={showRelationshipColumn ? props.raterLevels : undefined}
              projectLanguages={showLanguageColumn ? props.projectLanguages : undefined}
              formValues={values}
              disableSubmitBtn={disableSubmitEditBtn}
              emailFieldError={highlightEmailInput}
            />
          ) : (
            <RaterCard
              key={rater.raterId}
              css={[styles.card, raterUpdateFailedFor(rater) && styles.errorCard]}
              rater={rater}
              showNames={props.showNameFields}
              showRelationship={showRelationshipColumn}
              showLanguage={showLanguageColumn}
              approval={props.approval}
              disableEditBtn={raterUpdateFailedFor(rater)}
              disableDeleteBtn={raterUpdateFailedFor(rater)}
              inviteStatus={inviteStatus(
                rater.invitedDate,
                rater.undeliveredEmail,
                rater.statusMessageKey,
                props.approval,
                rater.approvalStatus,
                props.code,
              )}
              approvalElements={approvalCardElements(
                props.approval,
                props.hideApproverColumn,
                rater.approvalStatus,
                rater.levelId,
                props.approvalLevelId,
                props.approvalLevelCount,
                rater.raterId,
                props.setApprover,
                props.approverEmailSent,
              )}
              setRater={setUnderEdit}
              onConfirmDelete={() => props.deleteRater(rater.raterId)}
              approverEmailSent={props.approverEmailSent}
            />
          ),
        props.raters,
      );
    },
    [
      props,
      underEdit,
      raterUpdateFailedFor,
      setUnderEdit,
      showRelationshipColumn,
      showLanguageColumn,
      values,
      disableSubmitEditBtn,
      highlightEmailInput,
    ],
  );

  const mapRatersToRows  = useMemo(
    () => {
      const inEditMode = (raterId: number) => underEdit?.raterId === raterId;

      return R.map(
        (rater: Rater) =>
          inEditMode(rater.raterId) ? (
            <RaterEditableTableRow
              key={rater.raterId}
              css={[styles.table.row, raterUpdateFailedFor(rater) && styles.table.errorRow]}
              rater={rater}
              showNames={props.showNameFields}
              approval={props.approval}
              inviteStatus={inviteStatus(
                rater.invitedDate,
                rater.undeliveredEmail,
                rater.statusMessageKey,
                props.approval,
                rater.approvalStatus,
                props.code,
                )}
              approvalElements={approvalTableElements(
                props.approval,
                props.hideApproverColumn,
                rater.approvalStatus,
                rater.levelId,
                props.approvalLevelId,
                props.approvalLevelCount,
                rater.raterId,
                props.setApprover,
                props.approverEmailSent,
              )}
              setRater={setUnderEdit}
              cancelSetRater={props.cancelRaterEdit}
              raterLevels={showRelationshipColumn ? props.raterLevels : undefined}
              projectLanguages={showLanguageColumn ? props.projectLanguages : undefined}
              formValues={values}
              disableSubmitBtn={disableSubmitEditBtn}
              emailFieldError={highlightEmailInput}
            />
          ) : (
            <RaterTableRow
              key={rater.raterId}
              css={[styles.table.row, raterUpdateFailedFor(rater) && styles.table.errorRow]}
              rater={rater}
              showNames={props.showNameFields}
              showRelationship={showRelationshipColumn}
              showLanguage={showLanguageColumn}
              approval={props.approval}
              disableEditBtn={raterUpdateFailedFor(rater)}
              disableDeleteBtn={raterUpdateFailedFor(rater)}
              inviteStatus={inviteStatus(
                rater.invitedDate,
                rater.undeliveredEmail,
                rater.statusMessageKey,
                props.approval,
                rater.approvalStatus,
                props.code,
              )}
              approvalElements={approvalTableElements(
                props.approval,
                props.hideApproverColumn,
                rater.approvalStatus,
                rater.levelId,
                props.approvalLevelId,
                props.approvalLevelCount,
                rater.raterId,
                props.setApprover,
                props.approverEmailSent,
              )}
              setRater={setUnderEdit}
              onConfirmDelete={() => props.deleteRater(rater.raterId)}
              approverEmailSent={props.approverEmailSent}
            />
          ),
        props.raters,
      );
    },
    [
      props,
      underEdit,
      raterUpdateFailedFor,
      setUnderEdit,
      showRelationshipColumn,
      showLanguageColumn,
      values,
      disableSubmitEditBtn,
      highlightEmailInput,
    ],
  );

  return isMobile
    ? (
      <div>
        <fieldset css={css`padding: 0; margin: 0; border: 0 none;`}>
          <form onSubmit={handleSubmit} onChange={handleChange}>
            {mapRatersToCards}
          </form>
        </fieldset>
      </div>
    )
    : (
      <fieldset css={css`padding: 0; margin: 0; border: 0 none;`}>
        <form onSubmit={handleSubmit} onChange={handleChange}>
          <table css={[styles.table.root, css`text-align: ${props.tableAlign};`]}>
            <thead>
              <tr css={[styles.table.row, !isNilOrEmpty(props.raters) && styles.table.headerWithData]}>
                <th css={styles.cells.emailOrName}>
                  <Msg str={props.showNameFields ? Str.NameEmail : Str.Email} />
                </th>
                {showRelationshipColumn && <th><Msg str={Str.Relationship} /></th>}
                {showLanguageColumn && <th><Msg str={Str.Language} /></th>}
                <th><Msg str={Str.Invited} /></th>
                {props.approval && <th><Msg str={Str.Approval} /></th>}
                {props.approval && !props.hideApproverColumn && <th><Msg str={Str.Approver} /></th>}
                <th css={styles.cells.edit} />
                <th css={styles.cells.edit} />
              </tr>
            </thead>
            <tbody>
              {mapRatersToRows}
            </tbody>
          </table>
        </form>
      </fieldset>
    );
};

export default RaterList;
